import type {
  CustomerPaymentMethod,
  CustomerPaymentMethodOffer,
} from "@shared-core/data/customer-basket-types";
import {
  RadioGroup,
  RadioGroupItem,
} from "../../../app/components/primitives/radio-group";
import { Label } from "../../../app/components/primitives/label";
import { cn } from "../../../app/components/primitives/utils";

// How the customer pays, as the server offers it: only the methods this person may use are drawn (a method
// the server omits is not shown, disabled or explained, B5). A method that is offered but cannot be used says why,
// in the backend's words, under the tiles.
export function PaymentMethodChoice({
  methods,
  value,
  onChange,
  disabled = false,
  errorId,
}: {
  methods: CustomerPaymentMethodOffer[];
  /** Null: the server named no usable method, so none is chosen. */
  value: CustomerPaymentMethod | null;
  onChange: (method: CustomerPaymentMethod) => void;
  disabled?: boolean;
  /** The id of the message beside this input when the server refused the choice. */
  errorId?: string;
}) {
  const unusable = methods.filter((m) => !m.is_usable && m.unusable_reason);
  return (
    <div className="space-y-2">
      <RadioGroup
        value={value ?? ""}
        onValueChange={(next) => onChange(next as CustomerPaymentMethod)}
        disabled={disabled}
        aria-label="How would you like to pay?"
        aria-invalid={errorId ? true : undefined}
        aria-describedby={errorId}
        // Two equal tiles side by side once there is room; a lone method is one tile.
        className={cn("gap-3", methods.length > 1 && "sm:grid-cols-2")}
      >
        {methods.map((offer) => (
          <Label
            key={offer.method}
            htmlFor={`payment-${offer.method}`}
            className={cn(
              "items-center gap-3 rounded-lg border p-3 leading-normal font-normal",
              offer.is_usable ? "cursor-pointer" : "opacity-60",
              value === offer.method && "border-primary bg-primary/5",
            )}
          >
            <RadioGroupItem
              id={`payment-${offer.method}`}
              value={offer.method}
              disabled={disabled || !offer.is_usable}
              aria-describedby={
                offer.is_usable ? undefined : `payment-${offer.method}-reason`
              }
            />
            <span className="font-medium">{offer.label}</span>
          </Label>
        ))}
      </RadioGroup>
      {/* Why a method cannot be used, under the pair where it has the full width to read. */}
      {unusable.map((offer) => (
        <p
          key={offer.method}
          id={`payment-${offer.method}-reason`}
          className="text-sm text-muted-foreground"
        >
          {offer.unusable_reason}
        </p>
      ))}
    </div>
  );
}
